import React, { useMemo, useState } from "react";
import { DrawerForm } from "../../../components/drawers/DrawerForm";
import ToggleSwitch from "../../../components/form/ToggleSwitch";
import Icon from "../../../components/base/Icon";
import { useHub } from "../../../core/Global";
import { toastError, toastSuccess } from "../../../components/base/Toast";
import { CatalogOverview, CatalogPlan, CatalogRow, PlanFeature } from "../models/PlanCatalog";
import { toMoney, toRate } from "./format";
import TextBox from "../../../components/form/TextBox";


type Offer = "included" | "sold" | "none"

// Numbers are held as typed, so "0." and "" can sit in a field while it is being edited.
interface Form {
    name: string
    eyebrow: string
    tagline: string
    actionText: string
    sortOrder: string

    monthlyPrice: string
    annualPrice: string
    isCustomPriced: boolean
    priceText: string

    includedUsers: string
    includedMessages: string
    includedMinutes: string
    includedNumbers: string

    extraNumberPrice: string
    overageMessageRate: string
    overageMinuteRate: string

    whatsAppIncluded: boolean
    telegramIncluded: boolean
    extensions: Offer
    recordingRetentionMonths: string
    supportTier: string

    features: PlanFeature[]
    highlighted: boolean
    darkCard: boolean
    isActive: boolean
}

const offerOf = (included?: boolean, price?: number): Offer =>
    included ? "included" : (price ?? 0) > 0 ? "sold" : "none"

const formOf = (p: CatalogPlan): Form => ({
    name: p.name ?? "",
    eyebrow: p.eyebrow ?? "",
    tagline: p.tagline ?? "",
    actionText: p.actionText ?? "",
    sortOrder: String(p.sortOrder ?? 0),

    monthlyPrice: String(p.monthlyPrice ?? 0),
    annualPrice: String(p.annualPrice ?? 0),
    isCustomPriced: !!p.isCustomPriced,
    priceText: p.priceText ?? "",

    includedUsers: String(p.includedUsers ?? 0),
    includedMessages: String(p.includedMessages ?? 0),
    includedMinutes: String(p.includedMinutes ?? 0),
    includedNumbers: String(p.includedNumbers ?? 0),

    extraNumberPrice: String(p.extraNumberPrice ?? 0),
    overageMessageRate: String(p.overageMessageRate ?? 0),
    overageMinuteRate: String(p.overageMinuteRate ?? 0),

    whatsAppIncluded: !!p.whatsAppIncluded,
    telegramIncluded: !!p.telegramIncluded,
    extensions: offerOf(p.extensionsSupportIncluded, p.extensionsSupportPrice),
    recordingRetentionMonths: String(p.recordingRetentionMonths ?? 0),
    supportTier: p.supportTier ?? "",

    features: (p.features ?? []).map(f => ({ text: f.text ?? "", warn: !!f.warn })),
    highlighted: !!p.highlighted,
    darkCard: !!p.darkCard,
    isActive: !!p.isActive
})

// Compared by value, so "149" and "149.00" are not a change.
const numeric = new Set<keyof Form>([
    "sortOrder", "monthlyPrice", "annualPrice", "includedUsers", "includedMessages", "includedMinutes",
    "includedNumbers", "extraNumberPrice", "overageMessageRate", "overageMinuteRate", "recordingRetentionMonths"
])

const num = (v: string) => {
    const n = parseFloat(v)
    return isNaN(n) ? 0 : n
}

const int = (v: string) => {
    const n = parseInt(v, 10)
    return isNaN(n) ? 0 : n
}


// Editing one plan. Saved for new purchases only -- the drawer says so, and says how many
// customers it is leaving on their current terms.
export default function PlanEditDrawer(props: {
    row: CatalogRow,
    overview: CatalogOverview,
    onClose: () => void,
    onSaved: () => void
}) {

    const hub = useHub()

    const plan = props.row.plan
    const prices = props.overview.prices
    const isFree = plan.code === "free"

    const [form, formSet] = useState<Form>(formOf(plan))
    const original = useMemo(() => formOf(plan), [plan])

    const set = <K extends keyof Form>(key: K, value: Form[K]) => formSet(f => ({ ...f, [key]: value }))

    const monthly = num(form.monthlyPrice)
    const suggestedAnnual = Math.round(monthly * 12 * 0.9)

    const messageLow = num(form.overageMessageRate) < props.overview.messageFloor
    const minuteLow = num(form.overageMinuteRate) < props.overview.minuteFloor

    // What has moved since the drawer opened, by name.
    const changed = useMemo(() => {
        const labels: [keyof Form, string][] = [
            ["name", "Name"], ["eyebrow", "Label above the name"], ["tagline", "Tagline"], ["actionText", "Button"],
            ["sortOrder", "Position"], ["monthlyPrice", "Monthly price"], ["annualPrice", "Yearly price"],
            ["isCustomPriced", "Custom priced"], ["priceText", "Price text"], ["includedUsers", "Users at once"],
            ["includedMessages", "Messages a month"], ["includedMinutes", "Call minutes a month"],
            ["includedNumbers", "Phone numbers"], ["extraNumberPrice", "Extra phone number"],
            ["overageMessageRate", "Each message beyond"], ["overageMinuteRate", "Each minute beyond"],
            ["whatsAppIncluded", "WhatsApp"], ["telegramIncluded", "Telegram"], ["extensions", "Masked Job Calls"],
            ["recordingRetentionMonths", "Recordings kept for"],
            ["supportTier", "Support"], ["highlighted", "Best value ribbon"], ["darkCard", "Dark card"],
            ["isActive", "On sale"]
        ]
        const list = labels
            .filter(([k]) => numeric.has(k)
                ? num(original[k] as string) !== num(form[k] as string)
                : original[k] !== form[k])
            .map(([, l]) => l)
        if (JSON.stringify(original.features) !== JSON.stringify(form.features.filter(f => (f.text ?? "").trim())))
            list.push("Card features")
        return list
    }, [form, original])

    const save = (callback: any) => {
        const body = {
            code: plan.code,
            name: form.name,
            eyebrow: form.eyebrow,
            tagline: form.tagline,
            actionText: form.actionText,
            sortOrder: int(form.sortOrder),

            monthlyPrice: num(form.monthlyPrice),
            annualPrice: num(form.annualPrice),
            isCustomPriced: form.isCustomPriced,
            priceText: form.priceText,

            includedUsers: int(form.includedUsers),
            includedMessages: int(form.includedMessages),
            includedMinutes: int(form.includedMinutes),
            includedNumbers: int(form.includedNumbers),

            extraNumberPrice: num(form.extraNumberPrice),
            overageMessageRate: num(form.overageMessageRate),
            overageMinuteRate: num(form.overageMinuteRate),

            whatsAppIncluded: form.whatsAppIncluded,
            telegramIncluded: form.telegramIncluded,
            extensionsSupportIncluded: form.extensions === "included",
            extensionsSupportSold: form.extensions === "sold",
            // Every plan records every call: recording is not an add-on any more.
            callRecordingIncluded: true,
            callRecordingSold: false,
            recordingRetentionMonths: int(form.recordingRetentionMonths),
            supportTier: form.supportTier,

            features: form.features.filter(f => (f.text ?? "").trim()),
            highlighted: form.highlighted,
            darkCard: form.darkCard,
            isActive: form.isActive
        }

        hub.Post("/system/plancatalog/save", body, res => {
            callback(res)
            if (res.success) {
                const n = res.data?.lines?.length ?? 0
                toastSuccess(n === 0 ? "Nothing had changed." : `${plan.name} saved — ${n} change${n === 1 ? "" : "s"}, for new purchases.`)
                props.onSaved()
            }
            else toastError(res.message ?? "Couldn't save this plan.")
        }, () => callback({ success: false }))
    }

    const moveFeature = (i: number, by: number) => {
        const list = [...form.features]
        const j = i + by
        if (j < 0 || j >= list.length) return
        const [item] = list.splice(i, 1)
        list.splice(j, 0, item)
        set("features", list)
    }

    return (
        <DrawerForm id="drawer-plan-edit"
            title={"Edit " + (plan.name ?? plan.code)}
            titleIcon="pen"
            subtitle="Saved for new purchases only"
            size="large"
            buttonText="Save plan"
            validator={[{ name: "name", required: true, maxLength: 60 }]}
            confirmDisabled={changed.length === 0}
            onClose={props.onClose}
            onSubmit={(body, callback) => save(callback)}>

            <div className="catalog-edit">

                {/* ------------------------------------------------ price */}
                <div className="catalog-edit-section">
                    <div className="catalog-edit-title">Price</div>
                    <div className="catalog-edit-grid">
                        <NumberField label="Monthly" prefix="$" value={form.monthlyPrice}
                            disabled={form.isCustomPriced || isFree}
                            onChange={v => set("monthlyPrice", v)} />
                        <NumberField label="Yearly" prefix="$" value={form.annualPrice}
                            disabled={form.isCustomPriced || isFree}
                            hint={monthly > 0 && num(form.annualPrice) !== suggestedAnnual
                                ? <>10% off would be {toMoney(suggestedAnnual)} · <a href="#use" onClick={e => { e.preventDefault(); set("annualPrice", String(suggestedAnnual)) }}>use it</a></>
                                : monthly > 0 ? "12 months less 10%" : "0 = not sold yearly"}
                            onChange={v => set("annualPrice", v)} />
                    </div>
                    {
                        !isFree &&
                        <div className="catalog-edit-grid mt-4">
                            <div className="catalog-edit-toggle">
                                <ToggleSwitch small checked={form.isCustomPriced}
                                    onChange={v => set("isCustomPriced", v)} />
                                <div>
                                    <div className="fw-semibold">Custom priced</div>
                                    <div className="text-gray-500 fs-8">Quoted per customer, like Enterprise. Never sent on to subscribers.</div>
                                </div>
                            </div>
                            {
                                form.isCustomPriced &&
                                <TextField label="Shown in place of a price" value={form.priceText} placeholder="Custom"
                                    onChange={v => set("priceText", v)} />
                            }
                        </div>
                    }
                    {isFree && <div className="catalog-edit-note">Free has to stay free: an unpaid plan falls back to it.</div>}
                </div>

                {/* ------------------------------------------------ allowances */}
                <div className="catalog-edit-section">
                    <div className="catalog-edit-title">Included every period</div>
                    <div className="catalog-edit-grid is-4">
                        <NumberField label="Users at once" integer value={form.includedUsers}
                            onChange={v => set("includedUsers", v)} />
                        <NumberField label="Phone numbers" integer value={form.includedNumbers}
                            onChange={v => set("includedNumbers", v)} />
                        <NumberField label="Messages a month" integer value={form.includedMessages}
                            onChange={v => set("includedMessages", v)} />
                        <NumberField label="Call minutes a month" integer value={form.includedMinutes}
                            onChange={v => set("includedMinutes", v)} />
                    </div>
                </div>

                {/* ------------------------------------------------ overage */}
                <div className="catalog-edit-section">
                    <div className="catalog-edit-title">Beyond the allowance</div>
                    <div className="catalog-edit-grid is-3">
                        <NumberField label="Each message" prefix="$" value={form.overageMessageRate}
                            warn={messageLow ? `Below the pay-as-you-go ${toRate(props.overview.messageFloor)}` : undefined}
                            onChange={v => set("overageMessageRate", v)} />
                        <NumberField label="Each call minute" prefix="$" value={form.overageMinuteRate}
                            warn={minuteLow ? `Below the pay-as-you-go ${toRate(props.overview.minuteFloor)}` : undefined}
                            onChange={v => set("overageMinuteRate", v)} />
                        <NumberField label="Each extra number" prefix="$" suffix="/mo" value={form.extraNumberPrice}
                            onChange={v => set("extraNumberPrice", v)} />
                    </div>
                    <div className="catalog-edit-note">
                        Taken from the wallet as it is used. Extra users are {toMoney(prices.extraUser)} each, on Pro only and up to 15 users in all —
                        that price is under General prices.
                    </div>
                </div>

                {/* ------------------------------------------------ channels and add-ons */}
                <div className="catalog-edit-section">
                    <div className="catalog-edit-title">Channels &amp; add-ons</div>

                    <div className="catalog-edit-grid">
                        <div className="catalog-edit-toggle">
                            <ToggleSwitch small checked={form.whatsAppIncluded} onChange={v => set("whatsAppIncluded", v)} />
                            <div>
                                <div className="fw-semibold">WhatsApp included</div>
                                <div className="text-gray-500 fs-8">Otherwise sold at {toMoney(prices.whatsAppIntegration)}/mo</div>
                            </div>
                        </div>
                        <div className="catalog-edit-toggle">
                            <ToggleSwitch small checked={form.telegramIncluded} onChange={v => set("telegramIncluded", v)} />
                            <div>
                                <div className="fw-semibold">Telegram included</div>
                                <div className="text-gray-500 fs-8">Otherwise sold at {toMoney(prices.telegramIntegration)}/mo</div>
                            </div>
                        </div>
                    </div>

                    <OfferField label="Masked Job Calls" price={prices.maskedJobCalls} value={form.extensions}
                        onChange={v => set("extensions", v)} />

                    <div className="catalog-edit-grid mt-4">
                        <NumberField label="Recordings kept for" integer suffix="months" value={form.recordingRetentionMonths}
                            onChange={v => set("recordingRetentionMonths", v)} />
                        <TextField label="Support" value={form.supportTier} placeholder="Email, Priority, Named manager…"
                            onChange={v => set("supportTier", v)} />
                    </div>
                </div>

                {/* ------------------------------------------------ the card */}
                <div className="catalog-edit-section">
                    <div className="catalog-edit-title">The plan card</div>
                    <div className="catalog-edit-grid">
                        <TextField label="Name" value={form.name} onChange={v => set("name", v)} />
                        <TextField label="Label above the name" value={form.eyebrow} placeholder="For growing teams"
                            onChange={v => set("eyebrow", v)} />
                        <TextField label="Tagline" value={form.tagline} onChange={v => set("tagline", v)} />
                        <TextField label="Button" value={form.actionText} placeholder="Choose Pro"
                            onChange={v => set("actionText", v)} />
                        <NumberField label="Position" integer value={form.sortOrder} hint="Lower comes first"
                            onChange={v => set("sortOrder", v)} />
                    </div>

                    <div className="catalog-edit-label mt-5">Features listed on the card</div>
                    <div className="catalog-features">
                        {
                            form.features.map((f, i) =>
                                <div key={i} className={"catalog-feature" + (f.warn ? " is-warn" : "")}>
                                    <button type="button" className="catalog-feature-mark" title={f.warn ? "Shown as a warning" : "Shown with a tick"}
                                        onClick={e => {
                                            e.preventDefault()
                                            set("features", form.features.map((x, j) => j === i ? { ...x, warn: !x.warn } : x))
                                        }}>
                                        <Icon icon={f.warn ? "triangle-exclamation" : "check"} type="solid" size={11} />
                                    </button>
                                    <div className="flex-grow-1">
                                        <TextBox id={"plan-feature-" + i} name={"feature" + i} block noMarginBottom
                                            value={f.text ?? ""}
                                            onTextChanges={v => set("features", form.features.map((x, j) => j === i ? { ...x, text: v } : x))} />
                                    </div>
                                    <button type="button" className="btn btn-icon btn-sm btn-light" disabled={i === 0}
                                        onClick={e => { e.preventDefault(); moveFeature(i, -1) }}>
                                        <Icon icon="arrow-up" size={11} />
                                    </button>
                                    <button type="button" className="btn btn-icon btn-sm btn-light" disabled={i === form.features.length - 1}
                                        onClick={e => { e.preventDefault(); moveFeature(i, 1) }}>
                                        <Icon icon="arrow-down" size={11} />
                                    </button>
                                    <button type="button" className="btn btn-icon btn-sm btn-light-danger"
                                        onClick={e => { e.preventDefault(); set("features", form.features.filter((_, j) => j !== i)) }}>
                                        <Icon icon="xmark" size={11} />
                                    </button>
                                </div>
                            )
                        }
                        <button type="button" className="btn btn-sm btn-light-primary align-self-start"
                            disabled={form.features.length >= 20}
                            onClick={e => { e.preventDefault(); set("features", [...form.features, { text: "", warn: false }]) }}>
                            <Icon icon="plus" size={11} className="me-2" />
                            Add a feature
                        </button>
                    </div>

                    <div className="catalog-edit-grid mt-5">
                        <div className="catalog-edit-toggle">
                            <ToggleSwitch small checked={form.highlighted} onChange={v => set("highlighted", v)} />
                            <div className="fw-semibold">"Best value" ribbon</div>
                        </div>
                        <div className="catalog-edit-toggle">
                            <ToggleSwitch small checked={form.darkCard} onChange={v => set("darkCard", v)} />
                            <div className="fw-semibold">Dark card</div>
                        </div>
                        <div className="catalog-edit-toggle">
                            <ToggleSwitch small checked={form.isActive} disabled={!props.row.canDeactivate}
                                onChange={v => set("isActive", v)} />
                            <div>
                                <div className="fw-semibold">On sale</div>
                                <div className="text-gray-500 fs-8">
                                    {
                                        props.row.canDeactivate
                                            ? "Off: nobody new can choose it. Current subscribers keep it."
                                            : "Always on — the system relies on this plan."
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ------------------------------------------------ what saving does */}
                <div className={"catalog-edit-summary" + (changed.length > 0 ? " is-dirty" : "")}>
                    <Icon icon={changed.length > 0 ? "pen-to-square" : "circle-check"} type="solid" size={14} />
                    <div>
                        {
                            changed.length === 0
                                ? "Nothing changed yet."
                                : <><b>{changed.length} change{changed.length === 1 ? "" : "s"}:</b> {changed.join(", ")}.</>
                        }
                        <div className="mt-1 text-gray-600">
                            For new purchases only.
                            {
                                props.row.subscribers > 0 &&
                                <> The {props.row.subscribers} customer{props.row.subscribers === 1 ? "" : "s"} already on {plan.name} keep
                                    their current terms until you use <b>Send to subscribers</b>.</>
                            }
                        </div>
                    </div>
                </div>

            </div>

        </DrawerForm>
    )
}


// The project's own text box, so this form looks like every other form in the console.
const fieldName = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-")

function NumberField(props: {
    label: string,
    value: string,
    prefix?: string,
    suffix?: string,
    integer?: boolean,
    disabled?: boolean,
    hint?: React.ReactNode,
    warn?: string,
    onChange: (v: string) => void
}) {
    return (
        <div className="catalog-field">
            <TextBox id={"plan-" + fieldName(props.label)} name={fieldName(props.label)}
                label={props.label + (props.suffix ? ` (${props.suffix})` : "")} labelUp block noMarginBottom
                numeric={props.integer} decimal={!props.integer} readOnly={props.disabled}
                iconText={props.prefix} iconStart={!!props.prefix}
                value={props.value} onTextChanges={props.onChange} />
            {props.warn && <div className="catalog-field-warn"><Icon icon="triangle-exclamation" type="solid" size={10} /> {props.warn}</div>}
            {!props.warn && props.hint && <div className="catalog-field-hint">{props.hint}</div>}
        </div>
    )
}


function TextField(props: { label: string, value: string, placeholder?: string, onChange: (v: string) => void }) {
    return (
        <TextBox id={"plan-" + fieldName(props.label)} name={fieldName(props.label)} label={props.label}
            labelUp block noMarginBottom placeholder={props.placeholder}
            value={props.value} onTextChanges={props.onChange} />
    )
}


function OfferField(props: { label: string, price: number, value: Offer, onChange: (v: Offer) => void }) {
    const options: [Offer, string][] = [
        ["included", "Included"],
        ["sold", `Sold separately · ${toMoney(props.price)}/mo`],
        ["none", "Not available"]
    ]
    return (
        <div className="catalog-offer">
            <div className="fw-semibold">{props.label}</div>
            <div className="catalog-segments">
                {
                    options.map(([key, text]) =>
                        <button key={key} type="button"
                            className={"catalog-segment" + (props.value === key ? " is-active" : "")}
                            onClick={e => { e.preventDefault(); props.onChange(key) }}>
                            {text}
                        </button>
                    )
                }
            </div>
        </div>
    )
}
